import { Loader2, Lock, RefreshCw, Save, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RecallTimesheetDialog } from '../../RecallTimesheetDialog';
import { SubmitFooterSaveIndicator } from './SubmitFooterSaveIndicator';
import type { Timesheet } from '@/types/timesheet';

export interface SubmitFooterActionsProps {
  isReadOnly: boolean;
  isRejected: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  isDirty: boolean;
  lastSaved: Date | null;
  entriesCount: number;
  hasMissingProjects: boolean;
  hasMissingDescriptions: boolean;
  hasMissingCostCentres: boolean;
  hasMissingRoles: boolean;
  hasBlockingPolicyViolation: boolean;
  currentTimesheet: Timesheet;
  canRecallTimesheet: boolean;
  onSave: () => void;
  onSubmit: () => void;
  onResubmit: () => void;
  onRecall: (timesheetId: number, reason: string) => Promise<void>;
}

/** Save / Submit / Resubmit / Recall controls of the submit footer. */
export function SubmitFooterActions({
  isReadOnly,
  isRejected,
  isSaving,
  isSubmitting,
  isDirty,
  lastSaved,
  entriesCount,
  hasMissingProjects,
  hasMissingDescriptions,
  hasMissingCostCentres,
  hasMissingRoles,
  hasBlockingPolicyViolation,
  currentTimesheet,
  canRecallTimesheet,
  onSave,
  onSubmit,
  onResubmit,
  onRecall,
}: SubmitFooterActionsProps) {
  const submitDisabled =
    entriesCount === 0 ||
    hasMissingProjects ||
    hasMissingCostCentres ||
    hasMissingDescriptions ||
    hasMissingRoles ||
    isSaving ||
    isSubmitting;

  const blockedButton = (label: string, className: string, tourId?: string) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex cursor-not-allowed flex-1 sm:flex-none">
          <Button size="default" disabled className={className} data-tour={tourId}>
            <Lock className="h-4 w-4" />
            {label}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-center">
        <p className="font-semibold">Submission Blocked</p>
        <p className="text-xs mt-1">A policy violation is preventing submission. Open AI Insights for details.</p>
      </TooltipContent>
    </Tooltip>
  );

  return (
    <>
      {/* Recall Button - Only show when timesheet is awaiting approval (submitted) */}
      {currentTimesheet.status?.toLowerCase() === 'submitted' && (
        <RecallTimesheetDialog
          timesheet={currentTimesheet}
          onRecall={onRecall}
          disabled={!canRecallTimesheet}
        />
      )}

      {/* Save/Submit Buttons - Only show when NOT readonly */}
      {!isReadOnly && (
        <>
          <SubmitFooterSaveIndicator isSaving={isSaving} isDirty={isDirty} lastSaved={lastSaved} />
          <Button
            variant="outline"
            size="default"
            onClick={onSave}
            disabled={isSaving || isSubmitting || !isDirty}
            className="flex-1 sm:flex-none gap-2"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isSaving ? 'Saving...' : 'Save Draft'}
          </Button>
          {isRejected ? (
            hasBlockingPolicyViolation ? (
              blockedButton('Resubmit for Approval', 'w-full gap-2 font-semibold bg-orange-600/50 pointer-events-none')
            ) : (
              <Button
                size="default"
                onClick={onResubmit}
                disabled={submitDisabled}
                className="flex-1 sm:flex-none gap-2 font-semibold bg-orange-600 hover:bg-orange-700"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {isSubmitting ? 'Submitting...' : 'Resubmit for Approval'}
              </Button>
            )
          ) : hasBlockingPolicyViolation ? (
            blockedButton('Submit for Approval', 'w-full gap-2 font-semibold pointer-events-none', 'timesheet-submit')
          ) : (
            <Button
              size="default"
              onClick={onSubmit}
              disabled={submitDisabled}
              className="flex-1 sm:flex-none gap-2 font-semibold"
              data-tour="timesheet-submit"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
        </>
      )}
    </>
  );
}
